import React from 'react';
import { Sparkles, Cpu } from 'lucide-react';

const ImplicationsSection = () => {
  return (
    <section id="implications" style={{
      background: '#000000',
      padding: '100px 7.6923%',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        <h2 className="section-title" style={{
          fontSize: '48px',
          fontWeight: 600,
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
          color: '#FFFFFF',
          marginBottom: '60px',
          textAlign: 'center'
        }}>
          IMPLICATIONS
        </h2>

        <div className="implications-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '40px',
          marginBottom: '60px'
        }}>
          <div className="implication-card" style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            padding: '40px',
            transition: 'all 0.4s ease-in-out'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 255, 209, 0.1)';
            e.currentTarget.style.borderColor = '#00FFD1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
          }}>
            <Sparkles size={48} color="#00FFD1" style={{ marginBottom: '24px' }} />
            <h3 style={{
              fontSize: '28px',
              fontWeight: 600,
              color: '#FFFFFF',
              marginBottom: '20px'
            }}>
              Engineering Reality
            </h3>
            <p style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '16px'
            }}>
              If boundary-constrained selection determines which global histories are admissible, then what we call "the laws of physics" are not arbitrary decrees handed down from outside. They're the stable, self-consistent patterns that emerge in the history that actually gets realized.
            </p>
            <p style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '16px'
            }}>
              The bulk equations remain fixed—boundary selection doesn't rewrite fundamental operators. But as the universe evolves through different regimes, the effective behavior of these same underlying fields can shift dramatically. What appears impossible under current boundary conditions may become accessible as those conditions evolve toward Omega—not through exotic matter or violations of locality, but by understanding how to work within the optimization constraints that decide which solution corridors can physically exist.
            </p>
            <p style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)'
            }}>
              This isn't about hacking reality or engineering paradoxes. It's about recognizing that the space of admissible solutions may contain structure we haven't yet learned to access.
            </p>
          </div>

          <div className="implication-card" style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            padding: '40px',
            transition: 'all 0.4s ease-in-out'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 255, 209, 0.1)';
            e.currentTarget.style.borderColor = '#00FFD1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
          }}>
            <Cpu size={48} color="#00FFD1" style={{ marginBottom: '24px' }} />
            <h3 style={{
              fontSize: '28px',
              fontWeight: 600,
              color: '#FFFFFF',
              marginBottom: '20px'
            }}>
              Consciousness as Mechanism
            </h3>
            <p style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '16px'
            }}>
              In this framework, consciousness and computation aren't incidental. They're not decorative features that happen to arise in complex systems. They're the kinds of structures that can carry boundary information forward in time—building stable records, creating compressive models, generating the constraints that help determine which histories become real.
            </p>
            <p style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '16px'
            }}>
              We're not passive observers standing outside the universe, watching it unfold. We're parts of the universe that can encode it, compress it, and participate in its boundary data. Not because mind magically overrides matter, but because agents are one of the ways boundary conditions become physically realized.
            </p>
            <p style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)'
            }}>
              The universe doesn't require an external observer to make sense of itself. It can close the loop from within—and we're part of how that closure happens.
            </p>
          </div>
        </div>

        <div className="status-box" style={{
          background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.2), rgba(0, 255, 209, 0.05))',
          border: '2px solid #00FFD1',
          padding: '60px',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '32px',
            fontWeight: 600,
            color: '#FFFFFF',
            marginBottom: '24px'
          }}>
            The paper is complete. The predictions are logged. The experiments are underway.
          </h3>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            fontSize: '24px',
            fontWeight: 600,
            color: '#00FFD1'
          }}>
            STATUS: AUDIT PASSED
            <CheckCircle2 size={32} color="#00FFD1" />
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          section {
            padding: 60px 20px !important;
          }
          .section-title {
            font-size: 36px !important;
          }
          .implications-grid {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
          .implication-card {
            padding: 30px !important;
          }
          .status-box {
            padding: 40px !important;
          }
        }
        @media (max-width: 768px) {
          section {
            padding: 40px 20px !important;
          }
          .section-title {
            font-size: 32px !important;
          }
          .implication-card {
            padding: 20px !important;
          }
          .implication-card h3 {
            font-size: 24px !important;
          }
          .implication-card p {
            font-size: 16px !important;
          }
          .status-box {
            padding: 30px 20px !important;
          }
          .status-box h3 {
            font-size: 24px !important;
          }
          .status-box > div {
            font-size: 18px !important;
            flex-direction: column;
          }
        }
      `}</style>
    </section>
  );
};

const CheckCircle2 = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

export default ImplicationsSection;